import React from 'react'
import { Link } from 'react-router-dom'
import { ToolkitPageLayout } from '../../../components'
import {
  AuditFeedbackGuideImage,
  PatientSatisfactionSurveyImage,
  TimeToFirstDosageImage
  // PainManagementPatientSatisfactionPdf, // To be hosted externally
  // FinancialToolsInfusionClinicPptx, // To be hosted externally
} from 'assets'

interface Breadcrumb {
  label: string
  path: string
}

export const ClinicsAuditFeedbackPage: React.FC = () => {
  const pageTitle = 'Audit & Feedback'
  const breadcrumbs: Breadcrumb[] = [
    { label: 'Toolkit', path: '/toolkit' },
    { label: 'Clinics', path: '/toolkit/clinics' },
    { label: pageTitle, path: '/toolkit/clinics/audit-feedback' }
  ]

  // TODO: Replace with actual external URLs once files are hosted
  const painManagementPdfUrl = '#' // Placeholder
  const financialToolsPptxUrl = '#' // Placeholder

  return (
    <ToolkitPageLayout title={pageTitle} breadcrumbs={breadcrumbs}>
      <div className="prose max-w-none text-[#3C2416]">
        <p className="text-lg italic mb-8">
          The goal is feedback information to staff members in a structured,
          clear and concise manner based on data gathered prior to the audit.
          This will lead to sustained improvements and overall team building.
        </p>

        <hr className="my-8" />

        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          Audit & Feedback Guide
        </h2>
        <figure className="mb-8 p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
          <a
            href="https://docs.google.com/document/d/e/2PACX-1vQYuL3nmzV3fISUwl32AyncnIRDQpiDV9_j9yUqLfCU6bTWNnA7AsUKP_ilDt5oNw/pub"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
          >
            <img
              src={AuditFeedbackGuideImage}
              alt="Infusion Center (IC) audit and feedback guide screenshot"
              className="max-w-full h-auto rounded-md shadow-sm inline-block"
            />
          </a>
          <figcaption className="mt-2 text-sm text-gray-700">
            <em>Infusion Center (IC) audit and feedback</em>
          </figcaption>
        </figure>

        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          Patient Satisfaction Survey
        </h2>
        <figure className="mb-8 p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
          <a
            href="https://docs.google.com/document/d/e/2PACX-1vT-Y5L11xeVntXF9ViofmS8EHG7yrweRvs_0CPr07WpMHaNUMhqBhKfvztImxpN-g/pub"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
          >
            <img
              src={PatientSatisfactionSurveyImage}
              alt="Patient Satisfaction with Pain Management in Adults survey screenshot"
              className="max-w-full h-auto rounded-md shadow-sm inline-block"
            />
          </a>
          <figcaption className="mt-2 text-sm text-gray-700">
            <em>Patient Satisfaction with Pain Management in Adults</em>
          </figcaption>
        </figure>

        <div className="mb-8 p-4 border border-gray-200 rounded-lg shadow-md bg-white">
          <h3 className="text-xl font-semibold text-[#142841] mb-3">
            Pain Management and Patient Satisfaction
          </h3>
          <a
            href={painManagementPdfUrl} // Using placeholder URL
            target="_blank"
            rel="noopener noreferrer"
            // download attribute removed as it's an external link
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#A6410A] hover:bg-[#894d35] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A6410A] transition-colors duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
                clipRule="evenodd"
              />
            </svg>
            Download PDF
          </a>
          <p className="text-sm text-gray-600 mt-1">
            TODO: Update link to externally hosted PDF.
          </p>
        </div>

        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          Time to First Dosage & Admission Rate
        </h2>
        <figure className="mb-8 p-4 border border-gray-200 rounded-lg shadow-md bg-white text-center">
          <a
            href="https://docs.google.com/spreadsheets/d/e/2PACX-1vTOAOfW68y5fz9jL9y_0itJPQSNsrnT0hjLELXZHALk26Wc6ml25acJlDM3labvRw/pubhtml"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
          >
            <img
              src={TimeToFirstDosageImage}
              alt="Time to First Dosage-Admission Rate chart screenshot"
              className="max-w-full h-auto rounded-md shadow-sm inline-block"
            />
          </a>
        </figure>

        <h2 className="text-2xl font-semibold text-[#142841] mt-8 mb-6">
          Financial Tools for an Infusion Clinic
        </h2>
        <div className="mb-8 p-4 border border-gray-200 rounded-lg shadow-md bg-white">
          <a
            href={financialToolsPptxUrl} // Using placeholder URL
            target="_blank"
            rel="noopener noreferrer"
            // download attribute removed as it's an external link
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#A6410A] hover:bg-[#894d35] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A6410A] transition-colors duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
                clipRule="evenodd"
              />
            </svg>
            Download PPTX
          </a>
          <p className="text-sm text-gray-600 mt-1">
            TODO: Update link to externally hosted PPTX.
          </p>
        </div>

        <hr className="my-12" />

        {/* All Done Button */}
        <div className="mt-12 mb-8 text-center">
          <Link
            to="/toolkit/clinics" // Link to the main clinics page
            className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-6 py-3 text-lg font-medium transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            All done? Click here to return to the Clinics screen
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
